/**
 * React Query hooks for vehicles data
 * Provides data fetching, caching, and mutations for vehicles
 *
 * Feature 009: Workshop Booking & Job-Tracking System
 * T062: Add inline editing operations
 */

import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  vehiclesApi,
  type Vehicle,
  type VehicleWithOwner,
  type VehicleSearchParams,
  type CreateVehicleData,
  type UpdateVehicleData,
  type VehicleActivity,
} from '../services/vehiclesApi';
import { customerKeys } from './useCustomers';
import { api } from '../lib/api';

/**
 * Query keys for vehicles
 */
export const vehicleKeys = {
  all: ['vehicles'] as const,
  lists: () => [...vehicleKeys.all, 'list'] as const,
  list: (params: VehicleSearchParams) => [...vehicleKeys.lists(), params] as const,
  details: () => [...vehicleKeys.all, 'detail'] as const,
  detail: (id: string) => [...vehicleKeys.details(), id] as const,
  activity: (id: string) => [...vehicleKeys.all, 'activity', id] as const,
};

/**
 * Hook to fetch paginated list of vehicles
 */
export function useVehicleList(params: VehicleSearchParams = {}) {
  return useQuery({
    queryKey: vehicleKeys.list(params),
    queryFn: () => vehiclesApi.list(params),
    staleTime: 30_000,
  });
}

/**
 * Hook to fetch a single vehicle by ID
 */
export function useVehicle(id: string | undefined) {
  return useQuery({
    queryKey: vehicleKeys.detail(id!),
    queryFn: () => vehiclesApi.getById(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

/**
 * Hook to fetch vehicle activity timeline
 */
export function useVehicleActivity(vehicleId: string | undefined, limit = 20) {
  return useQuery<VehicleActivity[]>({
    queryKey: vehicleKeys.activity(vehicleId!),
    queryFn: () => vehiclesApi.getActivity(vehicleId!, limit),
    enabled: !!vehicleId,
    staleTime: 60_000,
  });
}

/**
 * Hook to create a new vehicle
 */
export function useCreateVehicle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateVehicleData) => vehiclesApi.create(data),
    onSuccess: (vehicle) => {
      queryClient.invalidateQueries({ queryKey: vehicleKeys.lists() });
      // Also invalidate customer vehicles list
      queryClient.invalidateQueries({ queryKey: customerKeys.vehicles(vehicle.customerId) });
      // Invalidate customer detail to refresh vehicles
      queryClient.invalidateQueries({ queryKey: customerKeys.detail(vehicle.customerId) });
    },
  });
}

/**
 * Hook to update a vehicle
 */
export function useUpdateVehicle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateVehicleData }) =>
      vehiclesApi.update(id, data),
    onSuccess: (vehicle) => {
      queryClient.setQueryData(vehicleKeys.detail(vehicle.id), vehicle);
      queryClient.invalidateQueries({ queryKey: vehicleKeys.lists() });
      // Also invalidate customer vehicles list
      queryClient.invalidateQueries({ queryKey: customerKeys.vehicles(vehicle.customerId) });
    },
  });
}

/**
 * Hook to delete a vehicle
 */
export function useDeleteVehicle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string; customerId: string }) => vehiclesApi.delete(id),
    onSuccess: (_data, { id, customerId }) => {
      queryClient.removeQueries({ queryKey: vehicleKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: vehicleKeys.lists() });
      // Also invalidate customer vehicles list
      queryClient.invalidateQueries({ queryKey: customerKeys.vehicles(customerId) });
      // Invalidate customer detail to refresh vehicles
      queryClient.invalidateQueries({ queryKey: customerKeys.detail(customerId) });
    },
  });
}

/**
 * Vehicle table item with version for inline editing
 */
export interface VehicleTableItem extends Vehicle {
  version: number;
  customer?: {
    id: string;
    name: string;
    phone: string;
  };
}

/**
 * Hook for vehicles list with pagination (T059)
 */
export function useVehiclesWithPagination(options: {
  search?: string;
  customerId?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
} = {}) {
  const {
    search = '',
    customerId,
    sortBy = 'updatedAt',
    sortDir = 'desc',
    limit = 20,
    offset = 0,
  } = options;

  const queryKey = ['vehicles', 'paginated', search, customerId, sortBy, sortDir, limit, offset];

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (customerId) params.append('customerId', customerId);
      params.append('sortBy', sortBy);
      params.append('sortDir', sortDir);
      params.append('limit', String(limit));
      params.append('offset', String(offset));

      return await api.get<{
        vehicles: VehicleTableItem[];
        total: number;
        limit: number;
        offset: number;
      }>(`/api/vehicles?${params.toString()}`);
    },
    staleTime: 30000,
  });

  return {
    vehicles: data?.vehicles || [],
    total: data?.total || 0,
    isLoading,
    isError,
    error: error as Error | null,
    refetch,
  };
}

/**
 * Hook for inline field updates with optimistic locking (T058)
 */
export function useVehicleInlineEdit() {
  const queryClient = useQueryClient();
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateField = useCallback(async (
    vehicleId: string,
    field: string,
    value: string,
    version: number
  ): Promise<VehicleTableItem> => {
    setIsUpdating(true);
    setError(null);

    try {
      const result = await api.patch<VehicleTableItem>(
        `/api/vehicles/${vehicleId}/inline`,
        { field, value: value || null },
        { headers: { 'If-Match': String(version) } }
      );

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });

      return result as VehicleTableItem;
    } catch (err) {
      const apiError = err as { code?: string; status?: number };

      if (apiError.status === 409 || apiError.code === 'VERSION_CONFLICT') {
        const conflictError = new Error('El registro fue modificado por otro usuario. Actualice la página.');
        setError(conflictError);
        throw conflictError;
      }

      setError(err as Error);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  }, [queryClient]);

  return {
    updateField,
    isUpdating,
    error,
  };
}

/**
 * Combined hook for vehicle list page with all operations (T062)
 */
export function useVehicleListPage() {
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('updatedAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [offset, setOffset] = useState(0);
  const limit = 20;

  const { vehicles, total, isLoading, error, refetch } = useVehiclesWithPagination({
    search,
    sortBy,
    sortDir,
    limit,
    offset,
  });

  const { updateField, isUpdating } = useVehicleInlineEdit();

  const handleSearchChange = useCallback((newSearch: string) => {
    setSearch(newSearch);
    setOffset(0);
  }, []);

  const handleSortChange = useCallback((newSortBy: string, newSortDir: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortDir(newSortDir);
  }, []);

  const handlePageChange = useCallback((newOffset: number) => {
    setOffset(newOffset);
  }, []);

  const handleFieldUpdate = useCallback(async (
    vehicleId: string,
    field: string,
    value: string,
    version: number
  ) => {
    const updated = await updateField(vehicleId, field, value, version);
    refetch();
    return updated;
  }, [updateField, refetch]);

  return {
    vehicles,
    total,
    isLoading: isLoading || isUpdating,
    error,
    search,
    sortBy,
    sortDir,
    offset,
    limit,
    onSearchChange: handleSearchChange,
    onSortChange: handleSortChange,
    onPageChange: handlePageChange,
    onFieldUpdate: handleFieldUpdate,
    refetch,
  };
}

// Re-export types for convenience
export type { Vehicle, VehicleWithOwner, VehicleSearchParams, CreateVehicleData, UpdateVehicleData };
